import { useCallback } from 'react';
import type { RebalancerPreset } from '@biasafe.models';
import type {
  Frequency,
  RebalScheduler,
  ReconScheduler,
  RecTiming,
  Weekday,
  WeekOfMonth,
} from '../RebalancingSchedule/types';
import {
  applyWindowDowngrade,
  normalizeReconScheduler,
  normalizeRebalScheduler,
} from '../RebalancingSchedule/utils/normalizeScheduler';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';

interface SchedulerShape {
  recon_scheduler: ReconScheduler;
  is_rebal_activated: boolean;
  rebal_scheduler: RebalScheduler;
}

// The universe-data preset (generated model) only carries the legacy schedule
// fields (frequency/month/week/weekday + align_to_business_day) — it has NO
// timing/ref_date/custom_lag_days/rebal_target yet. Map what it has and fill
// the rest with the canonical defaults so "Match to Universe" still produces a
// valid `scheduler` dict. BACKEND DEPENDENCY: once the preset exposes the new
// fields, read them directly here.
function alignToTiming(align?: {
  activated: boolean;
  periodEndMethod: string | null;
}): RecTiming {
  if (!align?.activated) return 'CUSTOM_INTRA_MONTH';
  return align.periodEndMethod === 'first' ? 'MONTH_START' : 'MONTH_END';
}

export function presetToScheduler(preset: RebalancerPreset): SchedulerShape {
  const recon = preset.reconstitution;
  const rebal = preset.rebalance;
  // Build the full shape, then normalize so inapplicable fields are null
  // (e.g. a D/W universe yields timing/first_month/week_of_month = null).
  return {
    recon_scheduler: normalizeReconScheduler({
      frequency: recon.reconFrequency as Frequency,
      timing: alignToTiming(recon.alignToBusinessDay),
      first_month: recon.firstReconMonth ?? null,
      week_of_month: (recon.weekOfMonth ?? null) as WeekOfMonth | null,
      weekday: recon.weekday as Weekday,
      ref_date: 'PREV_MONTH_END',
      custom_lag_days: null,
    }),
    is_rebal_activated: rebal.activated,
    rebal_scheduler: normalizeRebalScheduler({
      rebal_methodology: 'calendar_based',
      frequency: rebal.reconFrequency as Frequency,
      timing: alignToTiming(rebal.alignToBusinessDay),
      first_month: rebal.firstReconMonth ?? null,
      week_of_month: (rebal.weekOfMonth ?? null) as WeekOfMonth | null,
      weekday: rebal.weekday as Weekday,
      ref_date: null,
      custom_lag_days: null,
      rebal_target: 'use_existing',
    }),
  };
}

export function useStrategyPresets() {
  const setRebalanceScheduler = useSmartBetaStrategyStore(
    (s) => s.setRebalanceScheduler,
  );

  const setRebalancerForUniverse = useCallback(
    (preset: RebalancerPreset, startDate?: string | null) => {
      // Apply the preset AS THE WINDOW RUNS IT: a pre-2020 start can't run a
      // Daily/Weekly schedule (the US indices' preset carries a Daily
      // rebalance), and writing the raw preset only for the downgrade effect
      // to immediately coerce it made the store flash through an invalid
      // state — and un-synced the "Synced to Universe" badge.
      setRebalanceScheduler(
        applyWindowDowngrade(presetToScheduler(preset), startDate ?? null),
      );
    },
    [setRebalanceScheduler],
  );

  return { setRebalancerForUniverse };
}
